/**
 * /es/roadmap-de-producto — product roadmap concept page (Spanish). Targets the
 * one real ES PM keyword "que es un project roadmap". Links /es/product-discovery
 * + /es/registro-de-decisiones.
 */
import { MarketingShell } from './MarketingShell';

export function EsRoadmapProducto() {
  return (
    <MarketingShell
      currentLocale="es"
      hreflangPath="/roadmap-de-producto"
      hreflangLocales={['es']}
      documentTitle="Qué es un roadmap de producto (con ejemplo y plantilla) · Draftlize"
      documentDescription="Qué es un roadmap de producto, formatos y ejemplo, y cómo conectarlo con las decisiones para que no quede desactualizado cuando cambia el alcance."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guía · Roadmap</span>
          <h1 className="serif" style={{ fontSize: 'clamp(33px, 4.4vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            El roadmap de producto que sobrevive<br />
            <em>a los cambios de alcance.</em>
          </h1>
          <p className="lede">
            Un roadmap de producto muestra <em>hacia dónde</em> va el producto y <em>por qué</em>: no cada detalle, sino la dirección y el orden. Lo difícil no suele ser crearlo, sino que acaba como una «diapositiva congelada»: tres semanas después cambia una decisión, el roadmap no, y nadie vuelve a fiarse de él. Aquí, los formatos, un ejemplo, y cómo conectarlo con las decisiones para que siga siendo cierto.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Formatos de roadmap que funcionan.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Now / Next / Later</strong> — evita la falsa precisión de fechas; ideal al principio.</li>
            <li><strong>Por temas / resultados</strong> — ordena por objetivos, no por features.</li>
            <li><strong>Temporal (trimestres)</strong> — cuando las dependencias imponen fechas duras.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>Mini-ejemplo (Now/Next/Later):</strong> Now — reducir la fricción de onboarding · Next — facturación self-service · Later — espacios de equipo. Cada entrada lleva un <em>porqué</em>: la decisión que la puso en el roadmap. Eso es justo lo que pierde la versión en diapositivas.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Congelado vs vivo</span>
            <h2 className="serif">Roadmap-diapositiva <em>vs roadmap que vive con las decisiones.</em></h2>
          </div>
          <p>Un roadmap es solo la punta visible de una cadena de decisiones. Si los separas, envejece en silencio.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Roadmap conectado (Draftlize)</th>
              <th>Roadmap-diapositiva</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>El «porqué» de cada entrada</td><td className="ours yes">enlazado a una tarjeta de decisión</td><td className="no">en la cabeza / disperso</td></tr>
            <tr><td>Cuando cambia una decisión</td><td className="ours yes">la entrada se marca «stale»</td><td className="no">el roadmap sigue mal</td></tr>
            <tr><td>Tres semanas después</td><td className="ours yes">al día o señalado</td><td className="no">nadie se fía</td></tr>
            <tr><td>Un agente de IA lo usa</td><td className="ours yes">lee el contexto vía MCP</td><td className="partial">diapositiva plana</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          La mayoría de los roadmaps no estaban mal planificados: solo que nadie los actualizó en las tres semanas desde la última decisión.
        </blockquote>
        <cite>Conecta el roadmap con las decisiones y se actualiza solo cuando la decisión cambia. Eso es Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Preguntas frecuentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '¿Roadmap o backlog?', a: 'El roadmap muestra dirección y orden a nivel de resultados; el backlog es la lista priorizada de trabajo concreto. El roadmap responde «por qué / hacia dónde», el backlog «qué sigue».' },
            { q: '¿Qué horizonte temporal?', a: 'Hasta donde puedas dar dirección con honestidad; muchas veces Now/Next/Later en vez de trimestres fijos. La falsa precisión de fechas es la razón principal de que un roadmap pierda credibilidad.' },
            { q: '¿Cómo gestiono los cambios de alcance?', a: 'Si cambia la decisión detrás de una entrada, la entrada debe cambiar. En Draftlize ambas están enlazadas: la decisión modificada marca «stale» la parte afectada del roadmap.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 820, margin: '8px auto 0', fontSize: 15, color: 'var(--ink-1)' }}>
          Relacionado: <a href="/es/product-discovery" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>product discovery</a> · <a href="/es/registro-de-decisiones" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro de decisiones</a>
        </p>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un roadmap que se actualiza solo <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Regístrate y recibe $5</span>
          <span>Paga solo por lo que usas</span>
          <span>El saldo nunca caduca</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Construye tu roadmap como tarjetas enlazadas, no como diapositiva. Si cambia una decisión, el roadmap se actualiza solo — no tres semanas después.
          </p>
          <a className="btn-primary" href="/">Empieza gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
